import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { notification } from "antd";

import { useLazyDownloadSurveyViewQuery } from "@/redux/service";
import { RootState } from "@/redux/store";
import { clearDownload, setDownloadState } from "@/redux/downloads/reducer";
import BaseSvgRenderer from "@/components_v2/Svg/BaseSvgRenderer";
import SurveyIcon from "@/assets/icons/survey-icon.svg?react";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import { downloadPollingService } from "@/services/downloadPollingService";
import { UseSurveyViewDownloadOptions } from "@/views/survey-views/types";

export const useSurveyViewDownload = (
  options: UseSurveyViewDownloadOptions = {},
) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [downloadSurveyViewQuery] = useLazyDownloadSurveyViewQuery();

  const downloadingStates = useSelector(
    (state: RootState) => state.downloads.downloadingStates,
  );

  const { pollInterval = 2000, timeout = 300000 } = options;

  const showSuccessNotification = useCallback(() => {
    notification.success({
      message: t("surveyViews.labels.reportReady"),
      placement: "bottomRight",
    });
  }, [t]);

  const showErrorNotification = useCallback(() => {
    notification.error({
      message: t("surveyViews.labels.downloadFailed"),
      placement: "bottomRight",
    });
  }, [t]);

  const showTimeoutNotification = useCallback(() => {
    notification.warning({
      message: t("surveyViews.labels.downloadTimeout"),
      placement: "bottomRight",
    });
  }, [t]);

  const handleCleanup = useCallback(
    (surveyViewId: string) => {
      dispatch(clearDownload(surveyViewId));
    },
    [dispatch],
  );

  const downloadSurveyView = useCallback(
    async (surveyViewId: string) => {
      if (downloadingStates[surveyViewId]) {
        return;
      }

      dispatch(setDownloadState({ id: surveyViewId, isDownloading: true }));

      const createNotificationDescription = (filename: string) => (
        <div className="download-notification-container body-m-regular">
          <BaseSvgRenderer component={SurveyIcon} width="20" height="20" />
          <span>{filename}</span>
          <BaseSpinner size="extra-small" />
        </div>
      );

      await downloadPollingService.initiateDownload(
        surveyViewId,
        downloadSurveyViewQuery,
        { pollInterval, timeout },
        {
          onSuccess: () => {
            dispatch(
              setDownloadState({ id: surveyViewId, isDownloading: false }),
            );
            showSuccessNotification();
          },
          onError: () => {
            dispatch(
              setDownloadState({ id: surveyViewId, isDownloading: false }),
            );
            showErrorNotification();
          },
          onTimeout: () => {
            showTimeoutNotification();
          },
          onCleanup: handleCleanup,
        },
        {
          message: t("surveyViews.labels.generatingReport"),
          description: createNotificationDescription("Report.pdf"),
        },
      );
    },
    [
      downloadingStates,
      downloadSurveyViewQuery,
      dispatch,
      t,
      pollInterval,
      timeout,
      showSuccessNotification,
      showErrorNotification,
      showTimeoutNotification,
      handleCleanup,
    ],
  );

  const isDownloading = useCallback(
    (surveyViewId: string) => downloadingStates[surveyViewId] || false,
    [downloadingStates],
  );

  const cancelDownload = useCallback(
    (surveyViewId: string) => {
      downloadPollingService.stopPolling(surveyViewId);
      dispatch(clearDownload(surveyViewId));
    },
    [dispatch],
  );

  return {
    downloadSurveyView,
    isDownloading,
    downloadingStates,
    cancelDownload,
  };
};
